import React from 'react';
import {
  Box,
  Typography,
  TextField,
  FormControl,
  FormControlLabel,
  Select,
  MenuItem,
  InputLabel,
  Checkbox,
  FormGroup
} from '@mui/material';
import { Page } from '../../../types';

interface OtherSettingsTabProps {
  page: Page | null;
  ctaButtonVisible: boolean;
  companyInfoVisible: boolean;
  onCtaButtonVisibleChange: (visible: boolean) => void;
  onCompanyInfoVisibleChange: (visible: boolean) => void;
  ctaButtonId?: string; // 後方互換性のため残す
  ctaButtonIds?: string[]; // 複数選択
  onCtaButtonIdChange?: (id: string) => void; // 後方互換性のため残す
  onCtaButtonIdsChange?: (ids: string[]) => void; // 複数選択用
  editState?: any;
}

export const OtherSettingsTab: React.FC<OtherSettingsTabProps> = ({
  page,
  ctaButtonVisible,
  companyInfoVisible,
  onCtaButtonVisibleChange,
  onCompanyInfoVisibleChange,
  ctaButtonId,
  ctaButtonIds = [],
  onCtaButtonIdChange,
  onCtaButtonIdsChange,
  editState
}) => {
  // 利用可能なCTAボタン一覧を取得（新しい順で表示）
  const availableCtaButtons = React.useMemo(() => {
    const buttons = editState?.settings?.ctaButtons || [];
    const getCreatedTime = (id: string): number => {
      const num = String(id).replace(/^cta-/, '');
      const n = parseInt(num, 10);
      return Number.isNaN(n) ? 0 : n;
    };
    return [...buttons].sort((a: any, b: any) => getCreatedTime(b.id) - getCreatedTime(a.id));
  }, [editState?.settings?.ctaButtons]);
  
  // チェックボックスの変更ハンドラー
  const handleCtaButtonToggle = (buttonId: string, checked: boolean) => {
    if (!onCtaButtonIdsChange) return;
    
    let newIds: string[];
    if (checked) {
      newIds = [...ctaButtonIds, buttonId];
    } else {
      newIds = ctaButtonIds.filter(id => id !== buttonId);
    }
    
    onCtaButtonIdsChange(newIds);
    // 1つ以上選択されている場合はvisibleをtrueに
    onCtaButtonVisibleChange(newIds.length > 0);
  };
  return (
    <Box sx={{ pt: 0, px: 0, pb: 0 }}>
      <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
        CTAボタン設定
      </Typography>
      
      <Box sx={{ mb: 4 }}>
        {availableCtaButtons.length === 0 ? (
          <Typography variant="body2" color="error">
            利用可能なCTAボタンがありません。「POPUP CTA設定」タブでCTAボタンを作成してください。
          </Typography>
        ) : (
          <FormControl component="fieldset">
            <FormGroup>
              {availableCtaButtons.map((btn: any) => (
                <FormControlLabel
                  key={btn.id}
                  control={
                    <Checkbox
                      checked={ctaButtonIds.includes(btn.id)}
                      onChange={(e) => handleCtaButtonToggle(btn.id, e.target.checked)}
                      size="small"
                      sx={{
                        '&.Mui-checked': {
                          color: '#F49121',
                        },
                      }}
                    />
                  }
                  label={
                    <Typography variant="body2">
                      {btn.name}
                    </Typography>
                  }
                />
              ))}
            </FormGroup>
          </FormControl>
        )}
      </Box>
      
      <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
        会社概要表示設定
      </Typography>
      
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box
          onClick={() => {
            onCompanyInfoVisibleChange(!companyInfoVisible);
          }}
          sx={{
            position: 'relative',
            width: '48px',
            height: '24px',
            backgroundColor: companyInfoVisible ? '#1B76D2' : '#E0E0E0',
            borderRadius: '12px',
            cursor: 'pointer',
            transition: 'background-color 0.3s',
            display: 'flex',
            alignItems: 'center',
            padding: '2px',
          }}
        >
          <Box
            sx={{
              width: '20px',
              height: '20px',
              backgroundColor: '#FFFFFF',
              borderRadius: '50%',
              transform: companyInfoVisible ? 'translateX(24px)' : 'translateX(0px)',
              transition: 'transform 0.3s',
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
            }}
          />
        </Box>
        <Typography variant="body2" sx={{ fontSize: '14px', color: '#000000' }}>
          会社概要表示: {companyInfoVisible ? 'ON' : 'OFF'}
        </Typography>
      </Box>
    </Box>
  );
};

